import { Action, State, StateContext, Store } from '@ngxs/store';
import * as actions from './puzzle-game.actions';
import { InitTimeRecord } from './puzzle-time-record.actions';
import {
  LetterDeleteRestrictions,
  LetterOverwriteRestrictions,
  PuzzleGameStateModel,
  WordJumpOnRestrictions,
  WordJumpToRestrictions,
  DarkModes,
} from './puzzle-game-state.model';
import { patch } from '@ngxs/store/operators';
import { Injectable } from '@angular/core';
import { v1 as uuid1 } from 'uuid';
import { PuzzleGameBaseState } from '../puzzle-framework-module/puzzle-game-base.state';
import { HelperFunctionsService } from '../shared-module/helpers/helper-functions.service';

// @dynamic
@State<PuzzleGameStateModel>({
  name: 'puzzleGameState',
  defaults: {
    puzzleGames: {},
    paused: 0,
    autoZoomEnabled: undefined,
    definitionBarEnabled: undefined,
    highlightRightAndWrongEnabled: undefined,
    showErrorsImmediately: undefined,
    darkMode: DarkModes.System,
    highContrastEnabled: false,
    letterDeleteRestriction: LetterDeleteRestrictions.CorrectWords,
    letterOverwriteRestriction: LetterOverwriteRestrictions.None,
  },
})
@Injectable()
export class PuzzleGameState extends PuzzleGameBaseState {
  constructor(private store: Store, private helperFunctions: HelperFunctionsService) {
    super();
  }

  // private static filterGames(games, puzzleID, remove = true) {
  //   return games.filter(game => {
  //     return remove ? game.puzzleID !== puzzleID : game.puzzleID === puzzleID;
  //   });
  // }

  // @Action(actions.PuzzleGameMultiTabSync)
  // PuzzleGameMultiTabSync(store: StateContext<PuzzleGameStateModel>, { payload }: actions.PuzzleGameMultiTabSync) {
  //   store.setState(
  //     patch({
  //       puzzleGames: payload
  //     })
  //   );
  // }

  @Action(actions.ResetLetterSkipSettings)
  ResetLetterSkipSettings(store: StateContext<PuzzleGameStateModel>) {
    store.patchState({
      letterDeleteRestriction: LetterDeleteRestrictions.CorrectWords,
      letterOverwriteRestriction: LetterOverwriteRestrictions.None,
    });
  }

  @Action(actions.ResetLetterSkipSettingsForLeaderboard)
  ResetLetterSkipSettingsForLeaderboard(store: StateContext<PuzzleGameStateModel>) {
    store.patchState({
      letterDeleteRestriction: LetterDeleteRestrictions.CompletedWords,
      letterOverwriteRestriction: LetterOverwriteRestrictions.None,
    });
  }

  @Action(actions.InitDefaultValuesFromConfig)
  InitDefaultValuesFromConfig(store: StateContext<PuzzleGameStateModel>, action: actions.InitDefaultValuesFromConfig) {
    const currentState = store.getState();
    const patchObject: Partial<PuzzleGameStateModel> = {};

    if (action.payload.reset || currentState.highlightRightAndWrongEnabled === undefined) {
      patchObject.highlightRightAndWrongEnabled = action.payload.highlightRightAndWrongEnabled;
    }
    if (action.payload.reset || currentState.showErrorsImmediately === undefined) {
      patchObject.showErrorsImmediately = action.payload.showErrorsImmediately;
    }
    if (action.payload.reset || currentState.nativeKeyboard === undefined) {
      patchObject.nativeKeyboard = action.payload.nativeKeyboardEnabled;
    }
    if (action.payload.reset || currentState.keepKeyboardOpen === undefined) {
      patchObject.keepKeyboardOpen = action.payload.keepKeyboardOpen;
    }
    if (action.payload.reset || currentState.autoZoomEnabled === undefined) {
      patchObject.autoZoomEnabled = action.payload.autoZoomEnabled;
    }
    if (action.payload.reset || currentState.definitionBarEnabled === undefined) {
      patchObject.definitionBarEnabled = action.payload.definitionBarEnabled;
    }
    if (action.payload.reset || currentState.wordJumpOnRestriction === undefined) {
      patchObject.wordJumpOnRestriction = action.payload.wordJumpOnRestriction;
    }
    if (action.payload.reset || currentState.wordJumpToRestriction === undefined) {
      patchObject.wordJumpToRestriction = action.payload.wordJumpToRestriction;
    }

    store.patchState(patchObject);
  }

  @Action(actions.StartGame)
  StartGame(store: StateContext<PuzzleGameStateModel>, action: actions.InitPuzzleGame) {
    store.setState(
      patch({
        puzzleGames: patch({
          [action.payload.puzzleID]: patch({
            finished: false,
            started: true,
          }),
        }),
      })
    );
  }

  @Action(actions.PauseGame)
  PauseGame(store: StateContext<PuzzleGameStateModel>, action: actions.PauseGame) {
    const paused = store.getState().paused;
    store.setState(
      patch({
        paused: paused + 1,
      })
    );
  }

  @Action(actions.ResumeGame)
  ResumeGame(store: StateContext<PuzzleGameStateModel>, action: actions.ResumeGame) {
    const paused = store.getState().paused;
    store.setState(
      patch({
        paused: paused <= 0 ? 0 : paused - 1,
      })
    );
  }

  @Action(actions.ResetPause)
  ResetPause(store: StateContext<PuzzleGameStateModel>, action: actions.ResetPause) {
    store.setState(
      patch({
        paused: 0,
      })
    );
  }

  @Action(actions.InitPuzzleGame)
  InitPuzzleGame(state: StateContext<PuzzleGameStateModel>, action: actions.InitPuzzleGame) {
    state.setState(
      patch({
        puzzleGames: patch({ [action.payload.puzzleID]: action.payload }),
      })
    );
    this.store.dispatch(
      new InitTimeRecord({
        puzzleID: action.payload.puzzleID,
      })
    );
  }

  @Action(actions.SetPuzzleGame)
  SetPuzzleGame(state: StateContext<PuzzleGameStateModel>, action: actions.SetPuzzleGame) {
    state.setState(
      patch({
        puzzleGames: patch({ [action.payload.puzzleID]: action.payload }),
      })
    );
    // this.store.dispatch(
    //   new InitTimeRecord({
    //     puzzleID: action.payload.puzzleID,
    //   })
    // );
  }

  @Action(actions.DontShowSolutionDialogAgain)
  DontShowSolutionDialogAgain(store: StateContext<PuzzleGameStateModel>, action: actions.DontShowSolutionDialogAgain) {
    store.setState(
      patch({
        puzzleGames: patch({
          [action.payload.puzzleID]: patch({
            hideShowSolutionDialog: true,
          }),
        }),
      })
    );
  }

  @Action(actions.IncMoveCounter)
  IncMoveCounter(store: StateContext<PuzzleGameStateModel>, action: actions.IncMoveCounter) {
    const puzzleGames = store.getState().puzzleGames;
    if (!puzzleGames.hasOwnProperty(action.payload.puzzleID)) {
      return;
    }
    if (puzzleGames[action.payload.puzzleID].finished === true) {
      action.preventUpdate = true;
      return;
    }
    const moveCounter = puzzleGames[action.payload.puzzleID].moveCounter;
    store.setState(
      patch({
        puzzleGames: patch({
          [action.payload.puzzleID]: patch({
            moveCounter: moveCounter + 1,
          }),
        }),
      })
    );
  }

  @Action(actions.ConsumeJoker)
  ConsumeJoker(store: StateContext<PuzzleGameStateModel>, action: actions.ConsumeJoker) {
    const puzzleGames = store.getState().puzzleGames;
    if (!puzzleGames.hasOwnProperty(action.payload.puzzleID)) {
      return;
    }
    const jokerCount = puzzleGames[action.payload.puzzleID].jokerCount;

    store.setState(
      patch({
        puzzleGames: patch({
          [action.payload.puzzleID]: patch({
            jokerCount: jokerCount <= 0 ? 0 : jokerCount - 1,
          }),
        }),
      })
    );
  }

  @Action(actions.SetFinished)
  SetFinished(store: StateContext<PuzzleGameStateModel>, action: actions.SetFinished) {
    store.setState(
      patch({
        puzzleGames: patch({
          [action.payload.puzzleID]: patch({
            finished: true,
            started: true,
            winUUID: uuid1(),
          }),
        }),
      })
    );
  }

  @Action(actions.FoundSolutionWord)
  FoundSolutionWord(store: StateContext<PuzzleGameStateModel>, action: actions.FoundSolutionWord) {
    store.setState(
      patch({
        puzzleGames: patch({
          [action.payload.puzzleID]: patch({
            solutionFound: true,
            started: true,
            solutionUUID: uuid1(),
          }),
        }),
      })
    );
  }

  @Action(actions.FoundSaying)
  FoundSaying(store: StateContext<PuzzleGameStateModel>, action: actions.FoundSaying) {
    store.setState(
      patch({
        puzzleGames: patch({
          [action.payload.puzzleID]: patch({
            sayingFound: true,
            started: true,
          }),
        }),
      })
    );
  }

  @Action(actions.SetGameProgress)
  SetGameProgress(store: StateContext<PuzzleGameStateModel>, action: actions.SetGameProgress) {
    store.setState(
      patch({
        puzzleGames: patch({
          [action.payload.puzzleID]: patch({
            progress: action.payload.progress,
          }),
        }),
      })
    );
  }

  @Action(actions.SetGameDifficulty)
  SetGameDifficulty(store: StateContext<PuzzleGameStateModel>, action: actions.SetGameDifficulty) {
    store.setState(
      patch({
        difficulty: action.payload.difficulty,
      })
    );
  }

  @Action(actions.SetLanguage)
  SetLanguage(store: StateContext<PuzzleGameStateModel>, action: actions.SetLanguage) {
    store.setState(
      patch({
        language: action.payload.language,
      })
    );
  }

  @Action(actions.SetAutoZoomEnabled)
  SetAutoZoomEnabled(store: StateContext<PuzzleGameStateModel>, action: actions.SetAutoZoomEnabled) {
    store.setState(
      patch({
        autoZoomEnabled: action.payload.autoZoomEnabled,
      })
    );
  }

  @Action(actions.SetLetterOverwriteRestriction)
  SetLetterOverwriteRestriction(
    store: StateContext<PuzzleGameStateModel>,
    action: actions.SetLetterOverwriteRestriction
  ) {
    store.setState(
      patch({
        letterOverwriteRestriction: action.payload.letterOverwriteRestriction,
      })
    );
  }

  @Action(actions.SetSkipFilledFieldsEnabled)
  SetSkipFilledFieldsEnabled(store: StateContext<PuzzleGameStateModel>, action: actions.SetSkipFilledFieldsEnabled) {
    // letterOverrideRestrictions has three states, but this toggle is simplified to on and off
    if (action.payload.skipFilledFieldsEnabled) {
      store.setState(
        patch({
          letterOverwriteRestriction: LetterOverwriteRestrictions.All,
        })
      );
    } else {
      store.setState(
        patch({
          letterOverwriteRestriction: LetterOverwriteRestrictions.None,
        })
      );
    }
  }

  @Action(actions.SetLetterDeleteRestriction)
  SetLetterDeleteRestriction(store: StateContext<PuzzleGameStateModel>, action: actions.SetLetterDeleteRestriction) {
    store.setState(
      patch({
        letterDeleteRestriction: action.payload.letterDeleteRestriction,
      })
    );
  }

  @Action(actions.SetWordJumpOnRestriction)
  SetWordJumpOnRestriction(store: StateContext<PuzzleGameStateModel>, action: actions.SetWordJumpOnRestriction) {
    store.setState(patch({ wordJumpOnRestriction: action.payload.wordJumpOnRestriction }));
  }

  @Action(actions.SetWordJumpToRestriction)
  SetWordJumpToRestriction(store: StateContext<PuzzleGameStateModel>, action: actions.SetWordJumpToRestriction) {
    store.setState(patch({ wordJumpToRestriction: action.payload.wordJumpToRestriction }));
  }

  @Action(actions.SetWordJumpEnabled)
  SetWordJumpEnabled(store: StateContext<PuzzleGameStateModel>, action: actions.SetWordJumpEnabled) {
    // wordJumpOnRestriction has three states, but this toggle is simplified to on and off
    // It depends on highlightRightAndWrongEnabled
    if (action.payload.wordJumpEnabled) {
      // We are using assistance -> correct words
      if (store.getState().highlightRightAndWrongEnabled) {
        store.setState(
          patch({
            wordJumpOnRestriction: WordJumpOnRestrictions.CorrectWord,
            wordJumpToRestriction: WordJumpToRestrictions.IncorrectWord,
          })
        );
      } else {
        // We are not using assistance -> complete words
        store.setState(
          patch({
            wordJumpOnRestriction: WordJumpOnRestrictions.CompleteWord,
            wordJumpToRestriction: WordJumpToRestrictions.IncompleteWord,
          })
        );
      }
    } else {
      store.setState(
        patch({
          wordJumpOnRestriction: WordJumpOnRestrictions.None,
          wordJumpToRestriction: WordJumpToRestrictions.Any,
        })
      );
    }
  }

  @Action(actions.SetAutoMoveEnabled)
  SetAutoMoveEnabled(store: StateContext<PuzzleGameStateModel>, action: actions.SetAutoMoveEnabled) {
    // This simplifies auto-zoom and auto-move to on and off
    // It depends on highlightRightAndWrongEnabled
    if (action.payload.autoMoveEnabled) {
      // We are using assistance -> correct words
      if (store.getState().highlightRightAndWrongEnabled) {
        store.setState(
          patch({
            wordJumpOnRestriction: WordJumpOnRestrictions.CorrectWord,
            wordJumpToRestriction: WordJumpToRestrictions.IncorrectWord,
            autoZoomEnabled: true,
          })
        );
      } else {
        // We are not using assistance -> complete words
        store.setState(
          patch({
            wordJumpOnRestriction: WordJumpOnRestrictions.CompleteWord,
            wordJumpToRestriction: WordJumpToRestrictions.IncompleteWord,
            autoZoomEnabled: true,
          })
        );
      }
    } else {
      store.setState(
        patch({
          wordJumpOnRestriction: WordJumpOnRestrictions.None,
          wordJumpToRestriction: WordJumpToRestrictions.Any,
          autoZoomEnabled: false,
        })
      );
    }
  }

  @Action(actions.SetDefinitionBarEnabled)
  SetDefinitionBarEnabled(store: StateContext<PuzzleGameStateModel>, action: actions.SetDefinitionBarEnabled) {
    store.setState(
      patch({
        definitionBarEnabled: action.payload.definitionBarEnabled,
      })
    );
  }

  @Action(actions.SetKeepKeyboardOpen)
  SetKeepKeyboardOpen(store: StateContext<PuzzleGameStateModel>, action: actions.SetKeepKeyboardOpen) {
    store.setState(
      patch({
        keepKeyboardOpen: action.payload.keepKeyboardOpen,
      })
    );
  }

  @Action(actions.SetHighlightRightAndWrongEnabled)
  SetHighlightRightAndWrongEnabled(
    store: StateContext<PuzzleGameStateModel>,
    action: actions.SetHighlightRightAndWrongEnabled
  ) {
    store.setState(
      patch({
        highlightRightAndWrongEnabled: action.payload.highlightRightAndWrongEnabled,
      })
    );
  }

  @Action(actions.SetShowErrorsImmediately)
  SetShowErrorsImmediately(store: StateContext<PuzzleGameStateModel>, action: actions.SetShowErrorsImmediately) {
    store.setState(
      patch({
        showErrorsImmediately: action.payload.showErrorsImmediately,
      })
    );
  }

  @Action(actions.SetDarkMode)
  SetDarkMode(store: StateContext<PuzzleGameStateModel>, action: actions.SetDarkMode) {
    store.setState(patch({ darkMode: action.payload.darkMode }));
  }

  @Action(actions.SetDarkModeEnabled)
  SetDarkModeEnabled(store: StateContext<PuzzleGameStateModel>, action: actions.SetDarkModeEnabled) {
    if (action.payload.darkModeEnabled) {
      store.setState(patch({ darkMode: DarkModes.Dark }));
    } else {
      store.setState(patch({ darkMode: DarkModes.Light }));
    }
  }

  @Action(actions.SetHighContrastEnabled)
  SetHighContrastEnabled(store: StateContext<PuzzleGameStateModel>, action: actions.SetHighContrastEnabled) {
    store.setState(
      patch({
        highContrastEnabled: action.payload.highContrastEnabled,
      })
    );
  }

  @Action(actions.SetNativeKeyboardEnabled)
  SetNativeKeyboardEnabled(store: StateContext<PuzzleGameStateModel>, action: actions.SetNativeKeyboardEnabled) {
    store.setState(
      patch({
        nativeKeyboard: action.payload.nativeKeyboardEnabled,
      })
    );
  }
}
